import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Loader2, MailCheck, ArrowLeft } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';

const RESEND_COOLDOWN_MS = 30_000;

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Deliberately identical UX whether or not the email has an account, and
    // whether or not Supabase errored: no user enumeration via responses.
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        logger.error('resetPasswordForEmail failed', error, { component: 'ForgotPasswordPage' });
      }
    } catch (err) {
      logger.error('resetPasswordForEmail failed', err, { component: 'ForgotPasswordPage' });
    }

    setIsLoading(false);
    setSent(true);
    setCooldown(true);
    cooldownTimer.current = setTimeout(() => setCooldown(false), RESEND_COOLDOWN_MS);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">

        <Card className="backdrop-blur-xl shadow-2xl rounded-3xl">
          <CardHeader className="items-center text-center gap-4 pb-2">
            <div>
              <CardTitle className="text-2xl">Reset Password</CardTitle>
              <CardDescription className="mt-1">
                Enter your email and we&apos;ll send you a reset link
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="w-5 h-5 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="pl-11 h-12"
                  />
                </div>
              </div>

              {sent && (
                <Alert>
                  <MailCheck className="h-4 w-4" />
                  <AlertDescription>
                    If an account exists for that email, we&apos;ve sent a reset link.
                    Check your inbox (and spam folder).
                  </AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                disabled={isLoading || cooldown || !email}
                className="w-full h-12 text-base"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : cooldown ? (
                  'Link Sent — wait to resend'
                ) : sent ? (
                  'Resend Link'
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>
          </CardContent>

          <CardFooter className="justify-center">
            <Link
              to="/login"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to sign in
            </Link>
          </CardFooter>
        </Card>

        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl pointer-events-none opacity-50" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/20 rounded-full blur-3xl pointer-events-none opacity-30" />
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
